import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { THEME_IDS } from '@/themes/index';

// Carousel order: immersive + cultural first (travel/tourism focus), natgeo leads
const CAROUSEL_ORDER: string[] = [
  // Immersive/Cinematic — most visual impact for travel
  'natgeo', 'snow-fall', 'cinematic-dark', 'full-bleed-parallax', 'photo-essay-v2',
  'gallery-wall', 'zine-underground', 'bauhaus-minimal', 'swiss-international',
  'comic-strip', 'brutalist',
  // Cultural/Regional — diverse world perspectives
  'afrofuturist', 'afropunk-digital', 'latin-editorial', 'south-asian-vibrant',
  'mediterranean-warm', 'pan-african-earthy', 'nordic-minimal', 'colonial-archive',
  'middle-eastern-rtl', 'nikkei-grid',
  // Classic Print — prestigious editorial
  'vogue', 'rolling-stone', 'harpers-bazaar', 'new-yorker', 'the-atlantic',
  'economist', 'financial-times', 'scientific-american', 'time-magazine',
  // Digital-Native — modern reading
  'ghost-magazine', 'substack-letter', 'medium-reader', 'mirror-essay',
  'ghost-minimal', 'beehiiv-newsletter', 'notion-doc', 'readwise-highlights',
  'indie-log', 'paragraph-web3',
  // Functional/Specialist
  'travel-guide', 'recipe-lifestyle', 'sports-recap', 'event-liveblog',
  'qa-interview', 'podcast-v2', 'data-dashboard', 'annual-report',
  'academic-paper', 'legal-document',
  // Legacy (original 10)
  'page-flip', 'scrolling-long-form', 'magazine-grid', 'photo-essay',
  'news-column', 'timeline', 'minimal-one-pager', 'data-viz', 'grid',
  'podcast-transcript',
].filter((id) => THEME_IDS.includes(id));

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const STORAGE_IDX = 'reporter-carousel-idx';
const STORAGE_CHANGE_TS = 'reporter-carousel-change-ts';
const STORAGE_PAUSED_UNTIL = 'reporter-carousel-paused-until';

function readIdx(): number {
  const raw = localStorage.getItem(STORAGE_IDX);
  const n = parseInt(raw ?? '0', 10);
  return isNaN(n) ? 0 : Math.max(0, n % CAROUSEL_ORDER.length);
}

function readChangeTs(): number {
  const raw = localStorage.getItem(STORAGE_CHANGE_TS);
  const n = parseInt(raw ?? '0', 10);
  return isNaN(n) ? Date.now() : n;
}

function readPausedUntil(): number {
  const raw = localStorage.getItem(STORAGE_PAUSED_UNTIL);
  const n = parseInt(raw ?? '0', 10);
  return isNaN(n) ? 0 : n;
}

interface ThemeCarouselState {
  currentIndex: number;
  currentThemeId: string;
  nextThemeId: string;
  secondsUntilNext: number;
  isPaused: boolean;
  advance: () => void;
  pauseFor: (ms: number) => void;
}

const ThemeCarouselContext = createContext<ThemeCarouselState | null>(null);

export function ThemeCarouselProvider({ children }: { children: React.ReactNode }) {
  const [index, setIndex] = useState<number>(() => readIdx());
  const [changeTs, setChangeTs] = useState<number>(() => readChangeTs());
  const [pausedUntil, setPausedUntil] = useState<number>(() => readPausedUntil());
  const [now, setNow] = useState<number>(() => Date.now());
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Tick every second for countdown display
  useEffect(() => {
    tickRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, []);

  const advance = useCallback(() => {
    const next = (index + 1) % CAROUSEL_ORDER.length;
    const ts = Date.now();
    setIndex(next);
    setChangeTs(ts);
    localStorage.setItem(STORAGE_IDX, String(next));
    localStorage.setItem(STORAGE_CHANGE_TS, String(ts));
  }, [index]);

  const pauseFor = useCallback((ms: number) => {
    const until = Date.now() + ms;
    setPausedUntil(until);
    localStorage.setItem(STORAGE_PAUSED_UNTIL, String(until));
  }, []);

  // Auto-advance on 2-hour schedule
  useEffect(() => {
    if (now < pausedUntil) return;
    const elapsed = now - changeTs;
    if (elapsed >= TWO_HOURS_MS) {
      advance();
    }
  }, [now, changeTs, pausedUntil, advance]);

  // Cross-tab sync via storage event
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_IDX && e.newValue !== null) {
        const n = parseInt(e.newValue, 10);
        if (!isNaN(n)) setIndex(n);
      }
      if (e.key === STORAGE_CHANGE_TS && e.newValue !== null) {
        const n = parseInt(e.newValue, 10);
        if (!isNaN(n)) setChangeTs(n);
      }
      if (e.key === STORAGE_PAUSED_UNTIL && e.newValue !== null) {
        const n = parseInt(e.newValue, 10);
        if (!isNaN(n)) setPausedUntil(n);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const elapsed = now - changeTs;
  const remaining = Math.max(0, TWO_HOURS_MS - elapsed);
  const secondsUntilNext = Math.ceil(remaining / 1000);
  const isPaused = now < pausedUntil;

  const value: ThemeCarouselState = {
    currentIndex: index,
    currentThemeId: CAROUSEL_ORDER[index],
    nextThemeId: CAROUSEL_ORDER[(index + 1) % CAROUSEL_ORDER.length],
    secondsUntilNext,
    isPaused,
    advance,
    pauseFor,
  };

  return (
    <ThemeCarouselContext.Provider value={value}>
      {children}
    </ThemeCarouselContext.Provider>
  );
}

export function useThemeCarousel(): ThemeCarouselState {
  const ctx = useContext(ThemeCarouselContext);
  if (!ctx) throw new Error('useThemeCarousel must be used inside ThemeCarouselProvider');
  return ctx;
}

export { CAROUSEL_ORDER };
